<template>
  <div
    class="moniex-viewport"
    :class="{ 'about-bento-dark': isDarkMode }"
  >
    <div class="moniex-glow-orb"></div>

    <div class="moniex-bento-board" ref="boardRef" v-if="aboutData && aboutData.length >= 5" :style="boardStyle">

      <div class="moniex-premium-card card-story-54" ref="storyRef">
        <div class="card-header-moniex">
          <span class="card-title-main">{{ aboutData[0].AboutTitle }}</span>
          <span class="card-meta-badge badge-story">Příběh</span>
        </div>
        <div class="card-body-text">
          <div v-html="aboutData[0].AboutText"></div>
        </div>
      </div>

      <div class="moniex-premium-card card-account-mid" ref="accountRef" :style="accountStyle">
        <div class="moniex-icon-wrapper">
          <i class="ni ni-credit-card text-lg opacity-10 text-orange"></i>
        </div>
        <div class="moniex-card-content w-100">
          <span class="moniex-label">{{ aboutData[2].AboutTitle }}</span>
          <div class="api-html-content account-text-format" v-html="aboutData[2].AboutText"></div>
        </div>
      </div>

      <div class="moniex-premium-card card-contact-bottom" ref="mediaRef" :style="mediaStyle">
        <div class="moniex-icon-wrapper">
          <i class="ni ni-world-2 text-lg opacity-10 text-orange"></i>
        </div>
        <div class="moniex-card-content w-100">
          <span class="moniex-label">{{ mediaItem.AboutTitle }}</span>
          <div class="about-media-list">
            <a
              v-for="(soc, idx) in mediaLinks"
              :key="idx"
              class="about-social"
              :href="soc.url"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span class="about-social-icon">
                <fa-icon :icon="soc.icon" />
              </span>
              <span class="about-social-text">{{ soc.text }}</span>
            </a>
          </div>
        </div>
      </div>

      <div class="moniex-premium-card card-operator-top">
        <div class="moniex-icon-wrapper">
          <i class="ni ni-building text-lg opacity-10 text-orange"></i>
        </div>
        <div class="moniex-card-content">
          <span class="moniex-label">{{ aboutData[1].AboutTitle }}</span>
          <div class="api-html-content" v-html="aboutData[1].AboutText"></div>
        </div>
      </div>

      <div class="moniex-premium-card card-inga-54 img-inga-card" :style="ingaStyle">
        <div class="inga-speech-bubble">Pokud mě chceš kontaktovat, použij prosím Helpdesk.</div>
        <IngaPortrait :src="imgRoot + 'inga.jpg'" />
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from "vue";
import { useStore } from "vuex";
import dataService from "@/services/dataService.js";
import IngaPortrait from "./components/custom/IngaPortrait.vue";

const store = useStore();
const isDarkMode = computed(() => !!store.state.isDarkMode);
const aboutData = ref([]);
const imgRoot = computed(() => process.env.VUE_APP_IMG_ROOT || "/");

const mediaItem = computed(() => {
  return aboutData.value.find((row) => /m[eé]d/i.test(String(row.AboutTitle || "")))
    || aboutData.value[4]
    || { AboutTitle: "Média", AboutText: "" };
});

const socialIcon = (code) => {
  const c = String(code || "").toUpperCase();
  if (c === "IG" || c === "INSTAGRAM") return "fa-brands fa-instagram";
  if (c === "YT" || c === "YOUTUBE") return "fa-brands fa-youtube";
  if (c === "TT" || c === "TIKTOK") return "fa-brands fa-tiktok";
  if (c === "LI" || c === "LINKEDIN") return "fa-brands fa-linkedin";
  if (c === "TW" || c === "X") return "fa-brands fa-x-twitter";
  return "fa-brands fa-facebook";
};

const mediaLinks = computed(() => {
  const raw = String(mediaItem.value.AboutText || "").trim();
  if (!raw) return [];
  return raw.split(/\n+/).map((line) => {
    const parts = line.split("|").map((p) => p.trim()).filter(Boolean);
    if (parts.length < 2) return null;
    const code = parts[0];
    const text = parts.length >= 3 ? parts[1] : parts[0];
    const url = parts.length >= 3 ? parts[2] : parts[1];
    if (!url) return null;
    return { code, text, url, icon: socialIcon(code) };
  }).filter(Boolean);
});

const boardRef = ref(null);
const storyRef = ref(null);
const accountRef = ref(null);
const mediaRef = ref(null);
const accountTop = ref(340);
const mediaTop = ref(500);
const boardHeight = ref(1050);
const ingaHeight = ref(520);
const isNarrow = ref(false);

const accountStyle = computed(() => (isNarrow.value ? {} : { top: accountTop.value + "px" }));
const mediaStyle = computed(() => (isNarrow.value ? {} : { top: mediaTop.value + "px" }));
const boardStyle = computed(() => (isNarrow.value ? {} : { height: boardHeight.value + "px" }));
const ingaStyle = computed(() => (isNarrow.value ? {} : { "--inga-h": ingaHeight.value + "px" }));

const layoutStack = () => {
  if (typeof window === "undefined") return;
  isNarrow.value = window.innerWidth <= 992;
  if (isNarrow.value) return;
  const story = storyRef.value;
  const account = accountRef.value;
  const media = mediaRef.value;
  const board = boardRef.value;
  if (!story || !account || !media || !board) return;

  const gap = 24;
  const accTop = story.offsetTop + story.offsetHeight + gap;
  const accH = account.offsetHeight || 140;
  const medTop = accTop + accH + gap;
  const medH = media.offsetHeight || 180;

  accountTop.value = Math.round(accTop);
  mediaTop.value = Math.round(medTop);

  const ingaEl = board.querySelector(".card-inga-54");
  const ingaTop = ingaEl ? ingaEl.offsetTop : 270;
  const mediaBottom = medTop + medH;
  ingaHeight.value = Math.max(520, Math.round(mediaBottom - ingaTop));
  boardHeight.value = Math.round(Math.max(mediaBottom, ingaTop + ingaHeight.value) + 48);
};

let storyObserver = null;

onMounted(async () => {
  try {
    aboutData.value = (await dataService.GetAbout()) || [];
  } catch (error) {
    console.error("Chyba načítání About dat z API:", error);
  }
  await nextTick();
  layoutStack();
  if (typeof ResizeObserver !== "undefined") {
    storyObserver = new ResizeObserver(() => layoutStack());
    if (storyRef.value) storyObserver.observe(storyRef.value);
  }
  window.addEventListener("resize", layoutStack);
});

watch(aboutData, async () => {
  await nextTick();
  layoutStack();
});

onBeforeUnmount(() => {
  if (storyObserver) storyObserver.disconnect();
  window.removeEventListener("resize", layoutStack);
});
</script>

<style scoped>
.moniex-viewport {
  position: relative !important;
  background-color: #ffedd5 !important;
  background:
    radial-gradient(circle at 100% 0%, #fff7ed 0%, transparent 60%),
    radial-gradient(circle at 0% 100%, #fdba74 0%, transparent 70%),
    linear-gradient(135deg, #fff7ed 0%, #ffedd5 40%, #fed7aa 100%) !important;
  min-height: 100vh !important;
  padding: 40px !important;
  width: 100% !important;
  box-sizing: border-box !important;
  overflow-x: clip !important;
  overflow-y: visible !important;
  border-radius: 28px;
}

.about-bento-dark.moniex-viewport {
  background: #1E293B !important;
  background-color: #1E293B !important;
}

.moniex-glow-orb {
  position: absolute !important;
  bottom: -50px !important;
  left: -50px !important;
  width: 500px !important;
  height: 500px !important;
  background: radial-gradient(circle, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0) 70%) !important;
  filter: blur(50px) !important;
  z-index: 0 !important;
  pointer-events: none !important;
}

.about-bento-dark .moniex-glow-orb {
  background: radial-gradient(circle, rgba(15, 23, 42, 0.7) 0%, rgba(15, 23, 42, 0) 70%) !important;
}

.moniex-bento-board {
  position: relative !important;
  width: 100% !important;
  min-height: 700px !important;
  z-index: 1 !important;
}

.moniex-premium-card {
  position: absolute !important;
  background: #ffffff !important;
  border-radius: 28px !important;
  padding: 28px !important;
  box-shadow: 0 25px 50px rgba(154, 52, 0, 0.15) !important;
  border: none !important;
  display: flex !important;
  flex-direction: column !important;
  box-sizing: border-box !important;
}

.about-bento-dark .moniex-premium-card {
  background: #0F172A !important;
  box-shadow: none !important;
  border: 1px solid #334155 !important;
}

.about-bento-dark .card-operator-top,
.about-bento-dark .card-account-mid,
.about-bento-dark .card-contact-bottom {
  border: 1px solid #334155 !important;
}

.about-bento-dark .card-operator-top {
  border-left: 6px solid #5B82AC !important;
}

.about-bento-dark .card-account-mid {
  border-left: 6px solid #5A8E65 !important;
}

.about-bento-dark .card-contact-bottom {
  border-left: 6px solid #B3794D !important;
}

.moniex-premium-card:hover {
  transform: translateY(-4px) !important;
  box-shadow: 0 35px 70px rgba(154, 52, 0, 0.22) !important;
  z-index: 60 !important;
}

.about-bento-dark .moniex-premium-card:hover {
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.28) !important;
}

.card-inga-54:hover {
  transform: translateY(-4px) !important;
  box-shadow: 0 35px 70px rgba(154, 52, 0, 0.22) !important;
  z-index: 10 !important;
}

.about-bento-dark .card-inga-54:hover {
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.28) !important;
  z-index: 10 !important;
}

.card-story-54 {
  top: 20px !important;
  left: 20px !important;
  width: 54% !important;
  height: auto !important;
  min-height: 0 !important;
  z-index: 20 !important;
  background: #f8fafc !important;
}

.about-bento-dark .card-story-54 {
  background: #0F172A !important;
}

.card-inga-54 {
  top: 270px !important;
  left: 49% !important;
  width: 49% !important;
  height: var(--inga-h, 520px) !important;
  z-index: 10 !important;
  justify-content: flex-start !important;
  padding: 0 !important;
}

.card-inga-54 :deep(.inga-portrait-wrap) {
  width: 100%;
  height: 100%;
  flex: 1 1 auto;
  overflow: hidden;
}

.card-inga-54 :deep(.cartoon-img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
}

.about-bento-dark .card-inga-54 {
  background: #0F172A !important;
}

.card-operator-top {
  top: 50px !important;
  left: 62% !important;
  width: 360px !important;
  height: 140px !important;
  flex-direction: row !important;
  align-items: center !important;
  gap: 18px !important;
  z-index: 30 !important;
  border-left: 6px solid #5B82AC !important;
}

.card-account-mid {
  left: 16% !important;
  width: 380px !important;
  height: 140px !important;
  flex-direction: row !important;
  align-items: center !important;
  gap: 18px !important;
  z-index: 35 !important;
  border-left: 6px solid #5A8E65 !important;
}

.card-contact-bottom {
  left: 6% !important;
  width: 380px !important;
  min-height: 180px !important;
  height: auto !important;
  flex-direction: row !important;
  align-items: flex-start !important;
  gap: 18px !important;
  z-index: 36 !important;
  border-left: 6px solid #B3794D !important;
}

.moniex-icon-wrapper {
  background: #fff3e6 !important;
  width: 52px !important;
  height: 52px !important;
  border-radius: 16px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0 !important;
}

.about-bento-dark .moniex-icon-wrapper {
  background: #1E293B !important;
}

.moniex-icon-wrapper i {
  font-size: 22px !important;
}

.card-operator-top .moniex-icon-wrapper {
  background: rgba(13, 110, 253, 0.08) !important;
}
.card-operator-top .moniex-icon-wrapper i {
  color: #0d6efd !important;
}

.card-account-mid .moniex-icon-wrapper {
  background: rgba(40, 167, 69, 0.08) !important;
}
.card-account-mid .moniex-icon-wrapper i {
  color: #28a745 !important;
}

.card-contact-bottom .moniex-icon-wrapper {
  background: rgba(255, 107, 0, 0.08) !important;
}
.card-contact-bottom .moniex-icon-wrapper i {
  color: #ff6b00 !important;
}

.about-bento-dark .card-operator-top .moniex-icon-wrapper,
.about-bento-dark .card-account-mid .moniex-icon-wrapper,
.about-bento-dark .card-contact-bottom .moniex-icon-wrapper {
  background: #1E293B !important;
}

.card-header-moniex {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  margin-bottom: 12px !important;
}

.card-title-main {
  font-size: 1.1rem !important;
  font-weight: 700 !important;
  color: #1e293b !important;
}

.about-bento-dark .card-title-main {
  color: #E2E8F0 !important;
}

.card-meta-badge {
  font-size: 0.75rem !important;
  background: #e2e8f0 !important;
  color: #475569 !important;
  padding: 4px 12px !important;
  border-radius: 20px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
}

.badge-story {
  background: rgba(255, 107, 53, 0.14) !important;
  color: #ff6b35 !important;
}

.about-bento-dark .card-meta-badge {
  background: #1E293B !important;
  color: #94A3B8 !important;
}

.about-bento-dark .badge-story {
  background: rgba(255, 138, 76, 0.18) !important;
  color: #FF8A4C !important;
}

.card-body-text :deep(p),
.card-body-text :deep(div) {
  color: #334155 !important;
  line-height: 1.6 !important;
  font-size: 0.95rem !important;
  margin-bottom: 8px !important;
}

.about-bento-dark .card-body-text :deep(p),
.about-bento-dark .card-body-text :deep(div),
.about-bento-dark .card-body-text :deep(i) {
  color: #CBD5E1 !important;
  background: transparent !important;
}

.about-bento-dark .card-body-text :deep(strong) {
  color: #E2E8F0 !important;
}

.api-html-content :deep(strong) {
  font-weight: 800 !important;
  color: #0f172a !important;
}

.about-bento-dark .api-html-content :deep(strong) {
  color: #E2E8F0 !important;
}

.api-html-content :deep(p),
.api-html-content {
  font-size: 0.95rem !important;
  color: #334155 !important;
  line-height: 1.5 !important;
  margin: 0 !important;
}

.about-bento-dark .api-html-content,
.about-bento-dark .api-html-content :deep(p),
.about-bento-dark .api-html-content :deep(i) {
  color: #CBD5E1 !important;
}

.account-text-format :deep(strong) {
  font-size: 1.4rem !important;
  color: #0f172a !important;
  display: inline-block !important;
  margin-top: 4px !important;
  letter-spacing: 0.5px !important;
}

.about-bento-dark .account-text-format :deep(strong) {
  color: #E2E8F0 !important;
}

.moniex-label {
  font-size: 11px !important;
  color: #94a3b8 !important;
  font-weight: 700 !important;
}

.about-media-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.about-social {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none !important;
  color: #1E293B;
  font-weight: 600;
  font-size: 0.9rem;
}

.about-bento-dark .about-social {
  color: #E2E8F0;
}

.about-social:hover .about-social-text {
  color: #FF8A4C;
}

.about-social-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff3e6;
  color: #FF8A4C;
  flex-shrink: 0;
}

.about-bento-dark .about-social-icon {
  background: #1E293B;
  color: #FF8A4C;
}

.inga-speech-bubble {
  position: absolute !important;
  top: -45px !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  background-color: #ffffff !important;
  border: 2px solid #FF7A00 !important;
  color: #1E293B !important;
  font-weight: 700 !important;
  font-size: 0.85rem !important;
  padding: 12px 16px !important;
  border-radius: 18px !important;
  box-shadow: 0 10px 25px rgba(90, 30, 0, 0.08) !important;
  z-index: 15 !important;
  width: 90% !important;
  max-width: 290px !important;
  text-align: center !important;
}

.about-bento-dark .inga-speech-bubble {
  background-color: #1E293B !important;
  color: #E2E8F0 !important;
}

.inga-speech-bubble::after, .inga-speech-bubble::before {
  content: '' !important; position: absolute; bottom: -10px; left: 50%; transform: translateX(-50%); border-style: solid; display: block; width: 0;
}
.inga-speech-bubble::after { border-width: 10px 10px 0; border-color: #ffffff transparent transparent; z-index: 11; }
.inga-speech-bubble::before { bottom: -13px; border-width: 11px 11px 0; border-color: #FF7A00 transparent transparent; z-index: 9; }
.about-bento-dark .inga-speech-bubble::after { border-color: #1E293B transparent transparent; }

@media (max-width: 992px) {
  .moniex-bento-board {
    display: flex !important;
    flex-direction: column !important;
    height: auto !important;
  }
  .moniex-premium-card {
    position: relative !important;
    top: auto !important;
    left: auto !important;
    width: 100% !important;
    margin-bottom: 50px !important;
  }
}

@media (max-width: 430px) {
  .moniex-viewport {
    padding: 12px !important;
    border-radius: 16px;
  }
  .moniex-premium-card {
    margin-bottom: 16px !important;
    padding: 18px !important;
    border-radius: 18px !important;
  }
  .card-inga-54 {
    height: 340px !important;
  }
}
</style>
